import { atom } from "jotai";

// Derived Stripe Connect onboarding state (from GET /payout/status).
export type ConnectState = 'not_started' | 'incomplete' | 'complete' | 'restricted';

/**
 * Live health of the Shopify store, as reported by the backend probe.
 *
 * Distinct from `hasShopify`, which only says we hold usable credentials. A
 * deactivated store keeps its token and stays `hasShopify: true` — so its
 * settings page remains reachable — but reports 'store_unavailable' here.
 */
export type ShopifyHealthState = 'ok' | 'store_unavailable' | 'unauthorized' | 'unknown' | null;

/**
 * Frontend-derived capability flags.
 *
 * Nothing here is account-type driven — capabilities come from what the brand has
 * actually connected (Shopify) and completed (Stripe Connect onboarding).
 */
export interface ICapabilities {
    // True only when the brand-channel probe confirms a live Shopify connection.
    hasShopify: boolean;
    // False when the probe never answered (401, network error, backend down).
    // `hasShopify === false` is only meaningful when this is true — otherwise the
    // connection state is unknown and callers must not claim "not connected".
    shopifyKnown: boolean;
    /**
     * True when the brand has orders that did not originate from Shopify (internal
     * self-orders, API orders, interior-designer orders).
     *
     * Lets a storeless brand past the Shopify gate on the orders and customers pages:
     * it has real orders, so "connect a store to see them" would hide its own data.
     * Defaults to false, so a failed probe only ever gates more, never less.
     */
    hasNonShopifyOrders: boolean;
    // Licensing is never gated — every brand may price a design for the catalog.
    canLicense: boolean;
    connectState: ConnectState;
    connectOnboardingComplete: boolean;
    connectDisabledReason?: string | null;
    connectCurrentlyDue?: string[];
    // Shopify Plus gate for color variants. False until proven true.
    colorVariantsAllowed: boolean;
    /** Live store health. Null until the probe answers; only 'ok' means usable. */
    shopifyHealth: ShopifyHealthState;
    /** Merchant-facing explanation when health is not 'ok'. */
    shopifyHealthReason: string | null;
    /**
     * Whether the store granted the publications permissions needed to put new
     * products on the Online Store automatically. Null = unknown (never treat
     * as "missing").
     */
    publicationsScopeGranted: boolean | null;
}

export const capabilitiesAtom = atom<ICapabilities | null>(null);
export const capabilitiesLoadingAtom = atom<boolean>(false);

// Fail-closed default used whenever a probe errors out. `shopifyKnown: false`
// keeps "we could not ask" distinct from "the answer was no".
export const CAPABILITIES_FALLBACK: ICapabilities = {
    hasShopify: false,
    shopifyKnown: false,
    hasNonShopifyOrders: false,
    canLicense: true,
    connectState: 'not_started',
    connectOnboardingComplete: false,
    connectDisabledReason: null,
    connectCurrentlyDue: [],
    colorVariantsAllowed: false,
    shopifyHealth: null,
    shopifyHealthReason: null,
    publicationsScopeGranted: null,
};
